(() => {
  'use strict';
  if (window.location.pathname.replace(/\/+$/, '') === '/pages/build-your-tmo-collection') return;

  if (window.__tmoDeliveryInitialized) return;
  window.__tmoDeliveryInitialized = true;

  const ROOT_SELECTOR = '[data-tmo-delivery-root]';
  const EXCLUDED_VENDORS = new Set(['odmpod', 'tmo exclusive']);
  const BUILD_BOX_COLLECTION = '/collections/build-a-thailand-box';

  let refreshTimer;
  let refreshInFlight = false;
  let refreshQueued = false;

  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format((Number(cents) || 0) / 100);

  const isExcludedVendor = (vendor) =>
    EXCLUDED_VENDORS.has(String(vendor || '').trim().toLowerCase());

  function getQuote(subtotalCents) {
    if (!subtotalCents) return { state: 'app-managed', shippingCents: 0 };
    if (subtotalCents < 5000) return { state: 'ready', shippingCents: 3499 };
    if (subtotalCents < 7500) return { state: 'ready', shippingCents: 2999 };
    if (subtotalCents < 10000) return { state: 'ready', shippingCents: 2499 };
    if (subtotalCents < 15000) return { state: 'ready', shippingCents: 1999 };
    return { state: 'ready', shippingCents: 1499 };
  }

  function getOptimizationMessage(subtotalCents, shippingCents) {
    if (shippingCents <= 1499) return '<strong>You unlocked the best Thailand Express Delivery rate: $14.99.</strong>';
    let targetCents = 0;
    let nextShippingCents = 0;
    if (subtotalCents < 5000) { targetCents = 5000; nextShippingCents = 2999; }
    else if (subtotalCents < 7500) { targetCents = 7500; nextShippingCents = 2499; }
    else if (subtotalCents < 10000) { targetCents = 10000; nextShippingCents = 1999; }
    else if (subtotalCents < 15000) { targetCents = 15000; nextShippingCents = 1499; }
    if (!targetCents) return '';
    return `Add <strong>${money(targetCents - subtotalCents)}</strong> from <a class="tmo-delivery__link" href="${BUILD_BOX_COLLECTION}">Build a Thailand Box</a> to lower Thailand Express Delivery to <strong>${money(nextShippingCents)}</strong>.`;
  }

  function analyzeCart(cart) {
    let grocerySubtotalCents = 0;
    let hasAppManagedShipping = false;
    for (const item of cart.items || []) {
      if (item.requires_shipping === false) continue;
      if (isExcludedVendor(item.vendor)) { hasAppManagedShipping = true; continue; }
      grocerySubtotalCents += Number(item.final_line_price) || 0;
    }
    const quote = getQuote(grocerySubtotalCents);
    const estimatedTotalCents = quote.state === 'ready' ? (Number(cart.total_price) || 0) + quote.shippingCents : Number(cart.total_price) || 0;
    return { quote, grocerySubtotalCents, estimatedTotalCents, hasAppManagedShipping };
  }

  function buildMessage(analysis) {
    if (analysis.quote.state === 'app-managed') return 'These items use separate fulfillment. Their delivery charge will be shown clearly at checkout.';
    return getOptimizationMessage(analysis.grocerySubtotalCents, analysis.quote.shippingCents);
  }

  function renderRoot(root, cart, analysis) {
    const { quote } = analysis;
    const context = root.dataset.tmoContext || 'cart';
    const message = buildMessage(analysis);
    const hasGrocery = analysis.grocerySubtotalCents > 0;
    const mixed = hasGrocery && analysis.hasAppManagedShipping;
    const deliveryValue = quote.state === 'ready' ? money(quote.shippingCents) : 'Shown at checkout';
    const totalLabel = mixed ? 'Estimated total before separate app-managed delivery' : 'Estimated total before tax';
    const separateNote = analysis.hasAppManagedShipping ? '<small class="tmo-delivery__small">TMO Exclusive or app-fulfilled items ship separately and do not count toward Thailand grocery delivery tiers.</small>' : '';
    root.innerHTML = `<div class="tmo-delivery__card"><p class="tmo-delivery__eyebrow">U.S. delivery estimate</p><h2 class="tmo-delivery__title">${context === 'drawer' ? 'Your delivered total' : 'Your estimated delivered total'}</h2><div class="tmo-delivery__row"><span>Products</span><strong>${money(cart.total_price)}</strong></div><div class="tmo-delivery__row"><span>${hasGrocery ? 'Thailand Express Delivery' : 'Delivery'}</span><strong>${deliveryValue}</strong></div>${quote.state === 'ready' ? `<div class="tmo-delivery__row tmo-delivery__total"><span>${totalLabel}</span><span>${money(analysis.estimatedTotalCents)}</span></div>` : ''}${message ? `<p class="tmo-delivery__message">${message}</p>` : ''}${separateNote}<small class="tmo-delivery__small">Final tax and address eligibility are confirmed at checkout.</small></div>`;
  }

  function setCheckoutButton(button, cart, analysis) {
    if (!button) return;
    const blocked = Number(cart.item_count) === 0;
    button.disabled = blocked;
    button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    button.textContent = analysis.quote.state === 'ready' ? `CHECKOUT — ${money(analysis.estimatedTotalCents)}${analysis.hasAppManagedShipping ? ' + separate delivery' : ''}` : `CHECKOUT — ${money(cart.total_price)} + DELIVERY`;
  }

  function updateCheckoutButtons(cart, analysis) {
    setCheckoutButton(document.querySelector('#checkout'), cart, analysis);
    setCheckoutButton(document.querySelector('#CartDrawer-Checkout'), cart, analysis);
  }

  function ensureDrawerRoot() {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer || drawer.querySelector(ROOT_SELECTOR)) return false;
    const footer = drawer.querySelector('.drawer__footer');
    const ctas = drawer.querySelector('.cart__ctas');
    if (!footer) return false;
    const wrapper = document.createElement('div');
    wrapper.className = 'tmo-delivery tmo-delivery--drawer';
    wrapper.dataset.tmoDeliveryRoot = '';
    wrapper.dataset.tmoContext = 'drawer';
    wrapper.setAttribute('aria-live', 'polite');
    wrapper.innerHTML = '<div class="tmo-delivery__card tmo-delivery__card--loading"><p class="tmo-delivery__loading">Calculating your delivered total…</p></div>';
    if (ctas) footer.insertBefore(wrapper, ctas); else footer.prepend(wrapper);
    return true;
  }

  async function refresh() {
    if (refreshInFlight) { refreshQueued = true; return; }
    refreshInFlight = true;
    try {
      ensureDrawerRoot();
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      const analysis = analyzeCart(cart);
      const roots = document.querySelectorAll(ROOT_SELECTOR);
      roots.forEach((root) => renderRoot(root, cart, analysis));
      updateCheckoutButtons(cart, analysis);
      document.documentElement.classList.toggle('tmo-delivery-ready', roots.length > 0);
    } catch (error) {
      document.documentElement.classList.remove('tmo-delivery-ready');
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => { root.innerHTML = '<div class="tmo-delivery__card"><p class="tmo-delivery__message tmo-delivery__message--warning">Delivery estimate is temporarily unavailable. Your final rate will be shown before payment.</p></div>'; });
      console.error('[TMO delivery estimate]', error);
    } finally {
      refreshInFlight = false;
      if (refreshQueued) { refreshQueued = false; scheduleRefresh(50); }
    }
  }

  function scheduleRefresh(delay = 200) { window.clearTimeout(refreshTimer); refreshTimer = window.setTimeout(refresh, delay); }
  function isCartRequest(input) {
    const url = typeof input === 'string' ? input : input && typeof input.url === 'string' ? input.url : '';
    return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(url);
  }
  function installFetchHook() {
    if (window.__tmoDeliveryFetchHookInstalled) return;
    window.__tmoDeliveryFetchHookInstalled = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => { const response = await originalFetch(...args); if (isCartRequest(args[0])) scheduleRefresh(100); return response; };
  }
  function init() {
    installFetchHook();
    ensureDrawerRoot();
    refresh();
    document.addEventListener('change', (event) => { if (event.target.closest('cart-items, cart-drawer-items, #main-cart-items, #CartDrawer')) scheduleRefresh(); });
    document.addEventListener('click', (event) => { if (event.target.closest('cart-remove-button, .quantity__button, [name="updates[]"]')) scheduleRefresh(350); });
    const observer = new MutationObserver(() => { const inserted = ensureDrawerRoot(); if (inserted) scheduleRefresh(50); });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
